import React, { useState, useEffect, useCallback } from "react";
import DashboardLayout from "./DashboardLayout";
import { useUser } from "./Context/UserContext";

const MODE_OF_PAYMENT = { "1": "Cash", "2": "Bank Transfer", "3": "UPI" };
const PAYMENT_STATUS = { "1": "Non-Billable", "2": "Billable", "3": "Rejected" };

// --- REQUEST approval cascade: DM -> Finance ---
const reqStatusFor = (row) => {
    if (row.typeOfRequest === "Credit") {
        return "NOT REQUIRED";
    }

    if (Number(row.isFinanceApproved) === 1) {
        return "FINANCE APPROVED";
    }

    if (Number(row.isDMApproved) === 1) {
        return "DM APPROVED";
    }

    return "PENDING FOR DM APPROVAL";
};

// --- BILL approval: Credit -> Finance ---
const billStatusFor = (row) => {
    const isCredit = row.typeOfRequest === "Credit";

    // Petty Cash: bill can only be sent after request is fully approved.
    // Credit: no such gate — it goes straight to Finance for bill approval.
    if (!isCredit && (Number(row.isDMApproved) !== 1 || Number(row.isFinanceApproved) !== 1)) {
        return "NOT READY";
    }

    // Finance approved the bill
    if (Number(row.isBillFinanceApproved) === 1) {
        return "FINANCE APPROVED";
    }

    // Bill sent to Finance, awaiting approval
    if (Number(row.isBillSent) === 1) {
        return "PENDING FOR FINANCE APPROVAL";
    }

    // Approved (or Credit), but bill not sent yet
    return "PENDING FOR CREDIT TO SEND";
};


const getBillDetails = (row) => {
    const details =
        row?.billDetails ||
        row?.billItems ||
        row?.expenseDetails ||
        row?.expenses ||
        row?.items ||
        [];

    return Array.isArray(details) ? details : [];
};


const formatAmount = (value) => {
    return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};



const statusStyle = (text) => {
    const t = (text || "").toUpperCase();
    if (t.includes("APPROVED")) return { color: "#198754", backgroundColor: "#e8f5e9" };
    if (t.includes("PENDING")) return { color: "#b8860b", backgroundColor: "#fff8e1" };
    if (t.includes("REJECT")) return { color: "#dc3545", backgroundColor: "#fdecea" };
    return { color: "#6c757d", backgroundColor: "#f1f1f1" };
};

const StatusBadge = ({ text }) => (
    <span
        className="px-2 py-1 rounded-pill fw-semibold d-inline-block"
        style={{ ...statusStyle(text), fontSize: "0.78rem", letterSpacing: "0.02em" }}
    >
        {text}
    </span>
);

// Maps a modal action key to the endpoint + label used to submit it
const ACTION_CONFIG = {
    "dm-req": {
        endpoint: "dm-approve",
        label: "Delivery Manager — Request Approval"
    },

    "fin-req": {
        endpoint: "finance-approve",
        label: "Finance — Request Approval"
    },

    "dm-bill": {
        endpoint: "bill-dm-approve",
        label: "Delivery Manager — Bill Approval"
    },

    "fin-bill": {
        endpoint: "bill-finance-approve",
        label: "Finance — Bill Approval"
    }
};


// Best-effort display name for the logged-in user — adjust the field
// priority here if your UserContext uses a different shape.


// True if the logged-in user's roles array contains a role whose name
// includes `keyword` (case-insensitive) — e.g. hasRole(user, "delivery manager").
const hasRole = (user, keyword) =>
    Array.isArray(user?.roles) &&
    user.roles.some((r) => (r.role || "").toLowerCase().includes(keyword));

/**
 * role: "dm" | "finance" — which queue this page/route represents (matches
 * your existing DMPettCashRequests / FinancePettCashRequests menu split).
 * WHO can actually act is resolved from the logged-in user, not this prop:
 *   - "dm"      -> only rows where row.deliveryManager === this user's name
 *   - "finance" -> the shared finance queue (any row awaiting Finance)
 */
const PettyCashApproval = ({ role = "dm" }) => {
    const { user } = useUser();

    const currentUserName =
        user?.name ||
        user?.Name ||
        user?.username ||
        user?.userName ||
        user?.contactPersonName ||
        user?.roles?.[0]?.username ||
        "";

    console.log("DEBUG currentUserName:", currentUserName);
    console.log("DEBUG full user:", user);

    const isFinance = hasRole(user, "finance");

    // DM isn't a stored role — it's derived from whether this user
    // is named as deliveryManager on at least one request.
    const roleAllowed = role === "dm" ? true : isFinance;



    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal state
    const [modalRow, setModalRow] = useState(null);
    const [modalActionKey, setModalActionKey] = useState(null);// "dm-req" | "fin-req" | "fin-bill"
    const [approvalStatus, setApprovalStatus] = useState("Approve");
    const [comments, setComments] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const fetchRequests = useCallback(async () => {
        setLoading(true);

        try {
            const res = await fetch("/api/PettyCashCredit");
            const data = await res.json();

            console.log("PettyCashCredit API Response:", data);

            let requestList = [];

            // API returns an array directly
            if (Array.isArray(data)) {
                requestList = data;
            }

            // API returns { data: [...] }
            else if (Array.isArray(data?.data)) {
                requestList = data.data;
            }

            // API returns { result: [...] }
            else if (Array.isArray(data?.result)) {
                requestList = data.result;
            }

            // API returns { items: [...] }
            else if (Array.isArray(data?.items)) {
                requestList = data.items;
            }

            // API returns { requests: [...] }
            else if (Array.isArray(data?.requests)) {
                requestList = data.requests;
            }

            console.log("Final requestList:", requestList);
            console.log("Is array:", Array.isArray(requestList));

            // VERY IMPORTANT
            setRequests(Array.isArray(requestList) ? requestList : []);

        } catch (e) {
            console.error("Error loading petty cash requests:", e);
            setRequests([]);
        } finally {
            setLoading(false);
        }
    }, []);



    useEffect(() => {
        fetchRequests();
        const intervalId = setInterval(fetchRequests, 15000);
        return () => clearInterval(intervalId);
    }, [fetchRequests]);

    const openModal = (row, actionKey) => {
        setModalRow(row);
        setModalActionKey(actionKey);
        setApprovalStatus("Approve");
        setComments("");
    };

    const closeModal = () => {
        setModalRow(null);
        setModalActionKey(null);
        setComments("");
    };

    const handleSubmit = async () => {
        if (!modalRow || !modalActionKey) return;

        if (approvalStatus === "Reject") {
            alert(
                "Reject isn't wired to the backend yet — only Approve is currently supported. " +
                "Ask me to add a reject endpoint if you need this."
            );
            return;
        }

        const { endpoint } = ACTION_CONFIG[modalActionKey];

        setSubmitting(true);

        try {
            const res = await fetch(
                `/api/PettyCashCredit/${modalRow.id}/${endpoint}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ comments })
                }
            );

            const data = await res.json();

            if (!res.ok) {
                alert(data.message || "Action failed");
                return;
            }

            closeModal();
            await fetchRequests();

        } catch (e) {
            console.error("Approval error:", e);
            alert("Error processing approval");

        } finally {
            setSubmitting(false);
        }
    };

    // Visible rows depend on the ACTUAL logged-in identity, not a hardcoded prop:
    const visibleRequests = (Array.isArray(requests) ? requests : []).filter((row) => {

        if (role === "dm") {

            const normalizeWords = (str) =>
                String(str || "")
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(Boolean);

            const rowDMWords = normalizeWords(row.deliveryManager);
            const currentUserWords = normalizeWords(currentUserName);

            const assignedToCurrentDM =
                rowDMWords.length > 0 &&
                rowDMWords.every((word) =>
                    currentUserWords.includes(word)
                );

            if (!assignedToCurrentDM) {
                return false;
            }

            const requestWaitingForDM = Number(row.isDMApproved) !== 1;

            const billWaitingForDM =
                row.typeOfRequest !== "Credit" &&
                Number(row.isDMApproved) === 1 &&
                Number(row.isFinanceApproved) === 1 &&
                Number(row.isBillSent) === 1 &&
                Number(row.isBillDMApproved) !== 1;

            return requestWaitingForDM || billWaitingForDM;
        }

        // ================= FINANCE QUEUE =================

        const requestWaitingForFinance =
            row.typeOfRequest !== "Credit" &&
            Number(row.isDMApproved) === 1 &&
            Number(row.isFinanceApproved) !== 1;

        const isCredit = row.typeOfRequest === "Credit";

        // Credit bills are approved from Invoice Approval now — never surface them here.
        const billWaitingForFinance = !isCredit &&
            Number(row.isBillSent) === 1 &&
            Number(row.isBillDMApproved) === 1 &&
            Number(row.isBillFinanceApproved) !== 1;

        return requestWaitingForFinance || billWaitingForFinance;
    });
;


   

    return (
        <DashboardLayout>
            <div className="container-fluid py-3">

                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                    <div>
                        <h4 className="mb-1 fw-bold" style={{ color: "#14532d" }}>
                            Petty Cash Approval — {role === "dm" ? "Delivery Manager" : "Finance"}
                        </h4>
                        <div className="text-muted small">
                            {loading ? "Loading requests…" : `${visibleRequests.length} awaiting your action`}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                        onClick={fetchRequests}
                        disabled={loading}
                    >
                        <span>&#8635;</span> Refresh
                    </button>
                </div>

                {!roleAllowed && (
                    <div className="alert alert-warning">
                        Your account isn't assigned the {role === "dm" ? "Delivery Manager" : "Finance"} role,
                        so there's nothing to approve here.
                    </div>
                )}

                <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                    <div className="card-body p-0">
                        <table className="table table-hover mb-0 align-middle" style={{ fontSize: "0.92rem", tableLayout: "fixed", width: "100%" }}>                            <thead>
                                <tr style={{ backgroundColor: "#eaf6ef", color: "#14532d" }}>
                                <th className="py-3 ps-4" style={{ width: "12%" }}>Request ID</th>
                                <th className="py-3" style={{ width: "9%" }}>Type</th>
                                <th className="py-3" style={{ width: "12%" }}>Requested By</th>
                                <th className="py-3" style={{ width: "14%" }}>Delivery Manager</th>
                                <th className="py-3 text-end" style={{ width: "10%" }}>Amount</th>
                                <th className="py-3" style={{ width: "16%" }}>Req Status</th>
                                <th className="py-3" style={{ width: "16%" }}>Bill Status</th>
                                <th className="py-3 pe-4" style={{ width: "11%" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={8} className="text-center py-5 text-muted">Loading requests…</td></tr>
                                )}
                                {!loading && roleAllowed && visibleRequests.length === 0 && (
                                    <tr><td colSpan={8} className="text-center py-5 text-muted">Nothing waiting on your approval right now.</td></tr>
                                )}
                                {!loading && roleAllowed && visibleRequests.map((row) => {
                                    const reqStatus = reqStatusFor(row);
                                    const billStatus = billStatusFor(row);

                                    const canDmApproveReq =
                                        Number(row.isDMApproved) !== 1;

                                    const canDmApproveBill =
                                        row.typeOfRequest !== "Credit" &&
                                        Number(row.isDMApproved) === 1 &&
                                        Number(row.isFinanceApproved) === 1 &&
                                        Number(row.isBillSent) === 1 &&
                                        Number(row.isBillDMApproved) !== 1;

                                    const canFinanceApproveReq =
                                        Number(row.isDMApproved) === 1 &&
                                        Number(row.isFinanceApproved) !== 1;

                                    
                                    const canFinanceApproveBill = row.typeOfRequest !== "Credit" &&
                                        Number(row.isDMApproved) === 1 &&
                                        Number(row.isFinanceApproved) === 1 &&
                                        Number(row.isBillSent) === 1 &&
                                        Number(row.isBillDMApproved) === 1 &&
                                        Number(row.isBillFinanceApproved) !== 1;





                                    return (
                                        <tr key={row.id}>
                                            <td className="ps-4 fw-semibold" style={{ color: "#b8860b" }}>{row.reqId}</td>
                                            <td>{row.typeOfRequest}</td>
                                            <td>{row.requestedBy}</td>
                                            <td>{row.deliveryManager}</td>
                                            <td className="text-end fw-semibold">
                                                ₹{Number(row.amount).toLocaleString("en-IN")}
                                            </td>
                                            <td><StatusBadge text={reqStatus} /></td>
                                            <td><StatusBadge text={billStatus} /></td>
                                            <td className="pe-4" style={{ width: "90px", maxWidth: "90px", overflow: "visible" }}>
                                                <div className="d-flex flex-column gap-2" style={{ width: "100%", alignItems: "flex-start" }}>
                                                    {/* ================= DM REQUEST APPROVAL ================= */}
                                                    {role === "dm" && roleAllowed && canDmApproveReq && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm fw-semibold text-white"
                                                            style={{
                                                                backgroundColor: "#0d6efd", // "#198754" for bill buttons
                                                                borderRadius: "5px",
                                                                padding: "5px 10px",
                                                                fontSize: "12px",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                            onClick={() => openModal(row, "dm-req")}
                                                        >
                                                            Approve
                                                        </button>
                                                    )}

                                                    {/* ================= DM BILL APPROVAL ================= */}
                                                    {role === "dm" && roleAllowed && canDmApproveBill && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm fw-semibold text-white"
                                                            style={{
                                                                backgroundColor: "#0d6efd", // "#198754" for bill buttons
                                                                borderRadius: "5px",
                                                                padding: "5px 10px",
                                                                fontSize: "12px",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                            onClick={() => openModal(row, "dm-req")}
                                                        >
                                                            Approve
                                                        </button>
                                                    )}

                                                    {/* ================= FINANCE REQUEST APPROVAL ================= */}
                                                    {role === "finance" && roleAllowed && canFinanceApproveReq && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm fw-semibold text-white"
                                                            style={{
                                                                backgroundColor: "#0d6efd", // "#198754" for bill buttons
                                                                borderRadius: "5px",
                                                                padding: "5px 10px",
                                                                fontSize: "12px",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                            onClick={() => openModal(row, "dm-req")}
                                                        >
                                                            Approve
                                                        </button>
                                                    )}

                                                    {/* ================= FINANCE BILL APPROVAL ================= */}
                                                    {role === "finance" && roleAllowed && canFinanceApproveBill && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm fw-semibold text-white"
                                                            style={{
                                                                backgroundColor: "#0d6efd", // "#198754" for bill buttons
                                                                borderRadius: "5px",
                                                                padding: "5px 10px",
                                                                fontSize: "12px",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                            onClick={() => openModal(row, "dm-req")}
                                                        >
                                                            Approve
                                                        </button>
                                                    )}

                                                </div>
                                            </td>

                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ==================== APPROVAL MODAL ==================== */}
                {modalRow && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            position: "fixed",
                            inset: 0,
                            backgroundColor: "rgba(0,0,0,0.55)",
                            zIndex: 1050,
                            padding: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}

                        onClick={closeModal}
                    >
                        <div
                            className="bg-white rounded-4 shadow-lg"
                            style={{
                                width: "850px",
                                maxWidth: "calc(100vw - 40px)",
                                height: "auto",
                                maxHeight: "88vh",
                                overflowY: "auto",
                                overflowX: "hidden",
                                position: "relative",
                                margin: "auto"
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >


                            {/* ================= HEADER ================= */}
                            <div
                                className="px-4 py-3 border-bottom"
                                style={{
                                    backgroundColor: "#f8faf9",
                                    borderTopLeftRadius: "16px",
                                    borderTopRightRadius: "16px"
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-start">

                                    <div>
                                        <div
                                            className="small fw-semibold mb-1"
                                            style={{ color: "#198754" }}
                                        >
                                            PETTY CASH APPROVAL
                                        </div>

                                        <h5 className="mb-1 fw-bold text-dark">
                                            {ACTION_CONFIG[modalActionKey]?.label}
                                        </h5>

                                        <div className="small text-muted">
                                            Review the request details before approving.
                                        </div>
                                    </div>

                                    <div className="text-end">
                                        <div className="small text-muted mb-1">
                                            Request ID
                                        </div>

                                        <div
                                            className="fw-bold"
                                            style={{ color: "#b8860b" }}
                                        >
                                            {modalRow.reqId}
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* ================= BODY ================= */}
                            <div
                                className="p-4"
                                style={{
                                    overflowY: "auto"
                                }}
                            >


                                {/* REQUEST SUMMARY */}
                                <div
                                    className="rounded-3 p-3 mb-4"
                                    style={{
                                        backgroundColor: "#f8fafc",
                                        border: "1px solid #e5e7eb"
                                    }}
                                >
                                    <div className="row g-3">

                                        <div className="col-md-4">
                                            <div className="small text-muted">
                                                Request Type
                                            </div>

                                            <div className="fw-semibold">
                                                {modalRow.typeOfRequest}
                                            </div>
                                        </div>

                                        <div className="col-md-4">
                                            <div className="small text-muted">
                                                Requested By
                                            </div>

                                            <div className="fw-semibold">
                                                {modalRow.requestedBy}
                                            </div>
                                        </div>

                                        <div className="col-md-4">
                                            <div className="small text-muted">
                                                Employee ID
                                            </div>

                                            <div className="fw-semibold">
                                                {modalRow.employeeId}
                                            </div>
                                        </div>

                                    </div>
                                </div>


                                {/* ================= REQUEST DETAILS ================= */}
                                <div className="mb-2">
                                    <h6
                                        className="fw-bold mb-3"
                                        style={{ color: "#14532d" }}
                                    >
                                        Request Details
                                    </h6>
                                </div>

                                <div className="row g-3">

                                    {/* Project */}
                                    <div className="col-md-6">
                                        <label className="small text-muted mb-1">
                                            Project / Department
                                        </label>

                                        <input
                                            className="form-control"
                                            value={modalRow.project || ""}
                                            readOnly
                                        />
                                    </div>

                                    {/* Delivery Manager */}
                                    <div className="col-md-6">
                                        <label className="small text-muted mb-1">
                                            Delivery Manager
                                        </label>

                                        <input
                                            className="form-control"
                                            value={modalRow.deliveryManager || ""}
                                            readOnly
                                        />
                                    </div>

                                    {/* Vendor */}
                                    <div className="col-md-6">
                                        <label className="small text-muted mb-1">
                                            Vendor
                                        </label>

                                        <input
                                            className="form-control"
                                            value={modalRow.vendor || ""}
                                            readOnly
                                        />
                                    </div>

                                    {/* Payment Status */}
                                    <div className="col-md-6">
                                        <label className="small text-muted mb-1">
                                            Payment Status
                                        </label>

                                        <input
                                            className="form-control"
                                            value={
                                                PAYMENT_STATUS[modalRow.paymentStatus] ||
                                                modalRow.paymentStatus ||
                                                ""
                                            }
                                            readOnly
                                        />
                                    </div>

                                    {/* Mode of Payment */}
                                    <div className="col-md-6">
                                        <label className="small text-muted mb-1">
                                            Mode of Payment
                                        </label>

                                        <input
                                            className="form-control"
                                            value={
                                                MODE_OF_PAYMENT[modalRow.modeOfPayment] ||
                                                modalRow.modeOfPayment ||
                                                ""
                                            }
                                            readOnly
                                        />
                                    </div>

                                    {/* Request Date */}
                                    <div className="col-md-6">
                                        <label className="small text-muted mb-1">
                                            Create Date
                                        </label>

                                        <input
                                            className="form-control"
                                            value={modalRow.createDate || ""}
                                            readOnly
                                        />
                                    </div>

                                    {/* Amount */}
                                    <div className="col-md-6">
                                        <label className="small text-muted mb-1">
                                            Amount
                                        </label>

                                        <div
                                            className="form-control fw-bold"
                                            style={{
                                                color: "#14532d",
                                                backgroundColor: "#eaf6ef",
                                                borderColor: "#b7dec5",
                                                fontSize: "1.05rem"
                                            }}
                                        >
                                            ₹{Number(modalRow.amount || 0).toLocaleString("en-IN")}
                                        </div>
                                    </div>

                                </div>

                                {/* ==================== BILL DETAILS ==================== */}
                                {(
                                    
                                    modalActionKey === "fin-bill" ||
                                    modalActionKey === "dm-bill"
                                ) && (
                                        <div className="mt-4">

                                            <h6
                                                className="fw-bold mb-3"
                                                style={{ color: "#14532d" }}
                                            >
                                                Bill Details
                                            </h6>

                                            {getBillDetails(modalRow).length === 0 ? (
                                                <div className="alert alert-warning">
                                                    No bill details are available for this request.
                                                </div>
                                            ) : (
                                                <div
                                                    className="border rounded-3"
                                                    style={{ overflowX: "auto" }}
                                                >
                                                    <table
                                                        className="table table-bordered table-hover mb-0 align-middle"
                                                        style={{ fontSize: "0.85rem" }}
                                                    >
                                                        <thead>
                                                            <tr
                                                                style={{
                                                                    backgroundColor: "#eaf6ef",
                                                                    color: "#14532d"
                                                                }}
                                                            >
                                                                <th>Sl. No</th>
                                                                <th>Nature of Expense</th>
                                                                <th className="text-end">
                                                                    Amount
                                                                </th>
                                                                <th className="text-end">
                                                                    IGST
                                                                </th>
                                                                <th className="text-end">
                                                                    CGST
                                                                </th>
                                                                <th className="text-end">
                                                                    SGST
                                                                </th>
                                                                <th className="text-end">
                                                                    Total
                                                                </th>
                                                                <th>Document</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {getBillDetails(modalRow).map(
                                                                (item, index) => {

                                                                    const amount =
                                                                        Number(
                                                                            item.amount ??
                                                                            item.Amount ??
                                                                            item.expenseAmount ??
                                                                            0
                                                                        );

                                                                    const igst =
                                                                        Number(
                                                                            item.igst ??
                                                                            item.IGST ??
                                                                            0
                                                                        );

                                                                    const cgst =
                                                                        Number(
                                                                            item.cgst ??
                                                                            item.CGST ??
                                                                            0
                                                                        );

                                                                    const sgst =
                                                                        Number(
                                                                            item.sgst ??
                                                                            item.SGST ??
                                                                            0
                                                                        );

                                                                    const total =
                                                                        Number(
                                                                            item.total ??
                                                                            item.Total ??
                                                                            item.totalAmount ??
                                                                            (
                                                                                amount +
                                                                                igst +
                                                                                cgst +
                                                                                sgst
                                                                            )
                                                                        );

                                                                    const expenseName =
                                                                        item.natureOfExpense ??
                                                                        item.NatureOfExpenses ??
                                                                        item.natureOfExpenses ??
                                                                        item.expense ??
                                                                        item.description ??
                                                                        "-";

                                                                    const documentUrl =
                                                                        item.documentUrl ??
                                                                        item.document ??
                                                                        item.fileUrl ??
                                                                        item.attachmentUrl ??
                                                                        "";

                                                                    return (
                                                                        <tr
                                                                            key={
                                                                                item.id ||
                                                                                index
                                                                            }
                                                                        >
                                                                            <td>
                                                                                {index + 1}
                                                                            </td>

                                                                            <td className="fw-semibold">
                                                                                {expenseName}
                                                                            </td>

                                                                            <td className="text-end">
                                                                                ₹
                                                                                {formatAmount(
                                                                                    amount
                                                                                )}
                                                                            </td>

                                                                            <td className="text-end">
                                                                                ₹
                                                                                {formatAmount(
                                                                                    igst
                                                                                )}
                                                                            </td>

                                                                            <td className="text-end">
                                                                                ₹
                                                                                {formatAmount(
                                                                                    cgst
                                                                                )}
                                                                            </td>

                                                                            <td className="text-end">
                                                                                ₹
                                                                                {formatAmount(
                                                                                    sgst
                                                                                )}
                                                                            </td>

                                                                            <td
                                                                                className="text-end fw-bold"
                                                                                style={{
                                                                                    color: "#198754"
                                                                                }}
                                                                            >
                                                                                ₹
                                                                                {formatAmount(
                                                                                    total
                                                                                )}
                                                                            </td>

                                                                            <td>
                                                                                {documentUrl ? (
                                                                                    <a
                                                                                        href={
                                                                                            documentUrl
                                                                                        }
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="btn btn-sm btn-outline-primary"
                                                                                    >
                                                                                        View
                                                                                    </a>
                                                                                ) : (
                                                                                    <span className="text-muted">
                                                                                        No document
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                }
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                {/* ================= CURRENT STATUS ================= */}
                                <div className="mt-4">

                                    <h6
                                        className="fw-bold mb-3"
                                        style={{ color: "#14532d" }}
                                    >
                                        Current Approval Status
                                    </h6>

                                    <div className="row g-3">

                                        <div className="col-md-6">

                                            <div
                                                className="border rounded-3 p-3"
                                                style={{ backgroundColor: "#fafafa" }}
                                            >
                                                <div className="small text-muted mb-1">
                                                    Request Approval
                                                </div>

                                                <StatusBadge
                                                    text={reqStatusFor(modalRow)}
                                                />
                                            </div>

                                        </div>

                                        <div className="col-md-6">

                                            <div
                                                className="border rounded-3 p-3"
                                                style={{ backgroundColor: "#fafafa" }}
                                            >
                                                <div className="small text-muted mb-1">
                                                    Bill Approval
                                                </div>

                                                <StatusBadge
                                                    text={billStatusFor(modalRow)}
                                                />
                                            </div>

                                        </div>

                                    </div>

                                </div>


                                {/* ================= APPROVAL DECISION ================= */}
                                <div
                                    className="mt-4 p-3 rounded-3"
                                    style={{
                                        backgroundColor: "#f8fafc",
                                        border: "1px solid #dfe5e9"
                                    }}
                                >

                                    <h6
                                        className="fw-bold mb-3"
                                        style={{ color: "#14532d" }}
                                    >
                                        Approval Decision
                                    </h6>

                                    <label className="form-label small fw-semibold">
                                        Approval Status
                                        <span className="text-danger"> *</span>
                                    </label>

                                    <div className="d-flex gap-3">

                                        {/* APPROVE */}
                                        <button
                                            type="button"
                                            className={`btn flex-fill fw-semibold ${approvalStatus === "Approve"
                                                    ? "text-white"
                                                    : "btn-outline-success"
                                                }`}
                                            style={
                                                approvalStatus === "Approve"
                                                    ? {
                                                        backgroundColor: "#198754",
                                                        borderColor: "#198754"
                                                    }
                                                    : {}
                                            }
                                            onClick={() =>
                                                setApprovalStatus("Approve")
                                            }
                                            disabled={submitting}
                                        >
                                            ✓ Approve
                                        </button>

                                        {/* REJECT */}
                                        <button
                                            type="button"
                                            className={`btn flex-fill fw-semibold ${approvalStatus === "Reject"
                                                    ? "text-white"
                                                    : "btn-outline-danger"
                                                }`}
                                            style={
                                                approvalStatus === "Reject"
                                                    ? {
                                                        backgroundColor: "#dc3545",
                                                        borderColor: "#dc3545"
                                                    }
                                                    : {}
                                            }
                                            onClick={() =>
                                                setApprovalStatus("Reject")
                                            }
                                            disabled={submitting}
                                        >
                                            ✕ Reject
                                        </button>

                                    </div>


                                    {/* COMMENTS */}
                                    <div className="mt-3">

                                        <label className="form-label small fw-semibold">
                                            Comments
                                        </label>

                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            value={comments}
                                            onChange={(e) =>
                                                setComments(e.target.value)
                                            }
                                            placeholder="Enter comments or remarks..."
                                            disabled={submitting}
                                        />

                                    </div>

                                </div>

                            </div>


                            {/* ================= FOOTER ================= */}
                            <div
                                className="px-4 py-3 border-top d-flex justify-content-end gap-2"
                                style={{
                                    backgroundColor: "#fafafa",
                                    borderBottomLeftRadius: "16px",
                                    borderBottomRightRadius: "16px"
                                }}
                            >

                                <button
                                    type="button"
                                    className="btn btn-outline-secondary px-4"
                                    onClick={closeModal}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="btn px-4 fw-semibold text-white"
                                    style={{
                                        backgroundColor:
                                            approvalStatus === "Reject"
                                                ? "#dc3545"
                                                : "#198754"
                                    }}
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                >
                                    {submitting
                                        ? "Submitting..."
                                        : approvalStatus === "Reject"
                                            ? "Confirm Rejection"
                                            : "Confirm Approval"}
                                </button>

                            </div>

                        </div>
                    </div>
                )}


            </div>
        </DashboardLayout>
    );
};

export default PettyCashApproval;